/**
 * What this GPU can take for a 360° photo (ADR-041): whether it uploads raw
 * ASTC blocks as they are, and how large a cube map it allows. Read once from
 * the renderer at the start; `panoSource` decides from it which file a photo
 * is shown from. Pure over a small facade, so it is tested without WebGL.
 *
 * The extension is asked for directly, never through three's KTX2Loader,
 * whose detection turns ASTC off on the Quest browser (it says "X11; Linux"
 * without "Android", yet its Adreno GPU does ASTC in hardware).
 */

export const ASTC_EXTENSION = 'WEBGL_compressed_texture_astc';

export type PanoCaps = {
  /** WEBGL_compressed_texture_astc: the Quest, Apple Silicon. */
  readonly astc: boolean;
  /** `maxCubemapSize`: 2048 is WebGL 2's least; the Quest 3 and desktops allow far more. */
  readonly maxCubemapSize: number;
};

/** The GL facts the caps are read from. */
export type PanoGl = {
  hasExtension(name: string): boolean;
  readonly maxCubemapSize: number;
};

/** Without a renderer (a test, a headset with no GL): the JPEG path, the safe default. */
export const NO_PANO_CAPS: PanoCaps = { astc: false, maxCubemapSize: 0 };

export function panoCaps(gl: PanoGl): PanoCaps {
  return { astc: gl.hasExtension(ASTC_EXTENSION), maxCubemapSize: gl.maxCubemapSize };
}

/** The part of a `THREE.WebGLRenderer` the caps need. */
export type CapsRenderer = {
  readonly extensions: { has(name: string): boolean };
  readonly capabilities: { readonly maxCubemapSize: number };
};

const read = new WeakMap<CapsRenderer, PanoCaps>();

/** The renderer's caps, read the first time and kept for its life (the stage's: the app's). */
export function rendererCaps(renderer: CapsRenderer): PanoCaps {
  let caps = read.get(renderer);
  if (!caps) {
    caps = panoCaps({
      hasExtension: (name) => renderer.extensions.has(name),
      maxCubemapSize: renderer.capabilities.maxCubemapSize,
    });
    read.set(renderer, caps);
  }
  return caps;
}

/** `?perf=1`'s line for the caps. */
export function capsLine(caps: PanoCaps): string {
  return `GPU: ASTC ${caps.astc ? 'ja' : 'nee'} · kubus ≤ ${caps.maxCubemapSize}²`;
}

/** A page canvas's WebGL 2 context, as the stage's renderer would get. */
const pageContext = (): WebGL2RenderingContext | null =>
  document.createElement('canvas').getContext('webgl2');

/**
 * The caps before any lesson has a renderer: in the lobby, where a lesson is prepared ("Klaarzetten")
 * before the stage exists (it is built on the first lesson). A context of its own, read and let go
 * of at once, so the prefetch plan fetches the file the lesson will show. The same GPU answers the
 * same. `undefined` when nothing could be read (no WebGL, or a context refused for now): the caller
 * takes the JPEG path this time and asks again next time, never keeping a failure.
 */
export function probeCaps(
  make: () => WebGL2RenderingContext | null = pageContext,
): PanoCaps | undefined {
  let gl: WebGL2RenderingContext | null = null;
  try {
    gl = make();
    if (!gl) return undefined;
    const context = gl;
    return panoCaps({
      hasExtension: (name) => context.getExtension(name) !== null,
      maxCubemapSize: context.getParameter(context.MAX_CUBE_MAP_TEXTURE_SIZE) as number,
    });
  } catch {
    return undefined;
  } finally {
    try {
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      // Gone with its canvas anyway.
    }
  }
}
